import { Button, tokens } from "@blurb/codex-react";
import { createStyles, Textarea } from "@mantine/core";
import { GraphQL, GraphQLQueryData, isGraphQLError } from "@rpi/openapi-api";
import { RpiAlert, useToasts } from "@rpi/openapi-core";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { Body, Label } from "../../../../components/Typography";
import { RpaDropdown } from "../../../../components/inputFields/RpaDropdownInput";
import { RpaDrawer } from "../../../../rpi-core/drawer/RpaDrawer";
import { formatCurrency } from "../../../../utils/misc.util";
import { OrderItemSelector, ValueColumnConfig } from "../../OrderItemSelector";
import { buildReasonOptions } from "../../orderReasons";
import { RefundPreview } from "./RefundPreview";
import { useRefundPreview } from "./useRefundPreview";
import { getRefundOptionConfig, REFUND_OPTION_CONFIG, RefundUIStrategy } from "./refund.constants";
import {
  buildRefundRequest,
  getItemInputType,
  INITIAL_REFUND_FORM,
  RefundFormState,
  requiresItems,
  requiresReasonNote,
} from "./refund.utils";

const REASON_OPTIONS = buildReasonOptions();
const STRATEGY_OPTIONS = REFUND_OPTION_CONFIG.map(({ value, config }) => ({ value, label: config.label }));

type Order = GraphQLQueryData<GraphQL.OrderQuery>;

const useStyles = createStyles({
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[4],
    padding: `0 ${tokens.spacing[6]}`,
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: tokens.spacing[3],
    padding: tokens.spacing[6],
  },
  cancelLink: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    alignSelf: "center",
  },
  cancelText: {
    color: tokens.color.semantic.text.link,
    borderBottom: `1px solid currentColor`,
    paddingBottom: 2,
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[2],
  },
});

interface RefundDrawerProps {
  opened: boolean;
  onClose: () => void;
  order: Order | undefined;
}

export function RefundDrawer({ opened, onClose, order }: RefundDrawerProps) {
  const customerPublicId = order?.customer?.publicId;
  return (
    <RpaDrawer title={`Refund: ${order?.customerOrderId ?? ""}`} opened={opened} onClose={onClose}>
      {order &&
        opened &&
        (customerPublicId ? (
          <RefundDrawerBody order={order} customerPublicId={customerPublicId} onClose={onClose} />
        ) : (
          <RpiAlert>This order is missing customer information — refunds cannot be submitted.</RpiAlert>
        ))}
    </RpaDrawer>
  );
}

interface RefundDrawerBodyProps {
  order: Order;
  customerPublicId: string;
  onClose: () => void;
}

function RefundDrawerBody({ order, customerPublicId, onClose }: RefundDrawerBodyProps) {
  const { classes } = useStyles();
  const { showSuccessToast, showErrorToast } = useToasts();
  const queryClient = useQueryClient();

  const orderItems = order.orderItems ?? [];

  const [form, setForm] = React.useState<RefundFormState>(INITIAL_REFUND_FORM);
  const [submitted, setSubmitted] = React.useState(false);

  const onRefundSuccess = async () => {
    showSuccessToast("The refund was submitted successfully!");
    await queryClient.invalidateQueries({ queryKey: ["Order"] });
    await queryClient.invalidateQueries({ queryKey: ["OrderDetails"] });
    onClose();
  };

  const onRefundError = (error: unknown) => {
    showErrorToast(isGraphQLError(error) ? error.message : "Failed to submit refund. Please try again.");
  };

  const fullRefundMutation = GraphQL.useAdminRefundOrderMutation({
    onSuccess: onRefundSuccess,
    onError: onRefundError,
  });
  const byAmountMutation = GraphQL.useAdminRefundOrderByAmountMutation({
    onSuccess: onRefundSuccess,
    onError: onRefundError,
  });
  const byPercentageMutation = GraphQL.useAdminRefundOrderByPercentageMutation({
    onSuccess: onRefundSuccess,
    onError: onRefundError,
  });
  const byQuantityMutation = GraphQL.useAdminRefundOrderByQuantityMutation({
    onSuccess: onRefundSuccess,
    onError: onRefundError,
  });
  const isSubmitting =
    fullRefundMutation.isLoading ||
    byAmountMutation.isLoading ||
    byPercentageMutation.isLoading ||
    byQuantityMutation.isLoading;

  const buildResult = React.useMemo(
    () => buildRefundRequest(form, { customerOrderId: order.customerOrderId }),
    [form, order.customerOrderId]
  );

  const previewRequest = buildResult.ok ? buildResult.request : undefined;
  const preview = useRefundPreview(customerPublicId, previewRequest, order);
  const currency = order.currency ?? GraphQL.SupportedCurrency.Usd;

  const handleSubmit = () => {
    setSubmitted(true);
    if (!buildResult.ok) return;
    const { request } = buildResult;
    switch (request.kind) {
      case "byAmount":
        return byAmountMutation.mutate({ customerPublicId, input: request.input });
      case "byPercentage":
        return byPercentageMutation.mutate({ customerPublicId, input: request.input });
      case "byQuantity":
        return byQuantityMutation.mutate({ customerPublicId, input: request.input });
      case "fullRefund":
      case "shippingOnly":
        return fullRefundMutation.mutate({ customerPublicId, input: request.input });
    }
  };

  const reasonError = submitted && !form.reason ? "Please select a reason" : undefined;
  const strategyError = submitted && !form.strategy ? "Please select the refund option" : undefined;
  const reasonNoteError =
    submitted && requiresReasonNote(form.reason) && form.reasonNote.trim().length === 0
      ? "Please describe the reason"
      : undefined;

  const itemInputType = getItemInputType(form.strategy);
  const showItems = requiresItems(form.strategy);
  // Suppress item-level error until top-level fields validate, otherwise buildRefundRequest's reason cascades into the items slot.
  const canShowItemsError = submitted && showItems && !reasonError && !strategyError && !reasonNoteError;
  const itemsError = canShowItemsError && !buildResult.ok ? buildResult.reason : undefined;

  return (
    <>
      <div className={classes.body}>
        <RpaDropdown
          label={
            <Label size="base" weight="semibold">
              Reason
            </Label>
          }
          placeholder="Select.."
          searchable
          data={REASON_OPTIONS}
          value={form.reason ?? null}
          onChange={(value) =>
            setForm((prev) => ({ ...prev, reason: (value as GraphQL.RefundReason | null) ?? undefined }))
          }
          error={reasonError}
        />

        {requiresReasonNote(form.reason) && (
          <div className={classes.fieldGroup}>
            <Label size="base" weight="semibold" as="label" htmlFor="refund-reason-note">
              Reason Note
            </Label>
            <Textarea
              id="refund-reason-note"
              placeholder="Describe the reason for this refund"
              value={form.reasonNote}
              onChange={(event) => setForm((prev) => ({ ...prev, reasonNote: event.currentTarget.value }))}
              error={reasonNoteError}
              minRows={3}
            />
          </div>
        )}

        <RpaDropdown
          label={
            <Label size="base" weight="semibold">
              Refund Option
            </Label>
          }
          placeholder="Select.."
          data={STRATEGY_OPTIONS}
          value={form.strategy ?? null}
          onChange={(value) =>
            setForm((prev) => ({
              ...prev,
              strategy: (value as RefundUIStrategy | null) ?? undefined,
              items: [],
            }))
          }
          error={strategyError}
        />

        {showItems && (
          <OrderItemSelector
            orderItems={orderItems}
            rows={form.items}
            onRowsChange={(items) => setForm((prev) => ({ ...prev, items }))}
            valueColumn={getValueColumn(form.strategy)}
            error={itemsError}
            emptyState={
              <RpiAlert>
                This order has no items eligible for a per-item refund. Use Shipping Only or Full Refund instead.
              </RpiAlert>
            }
          />
        )}

        {previewRequest && (
          <RefundPreview
            preview={preview}
            order={order}
            inputType={itemInputType}
            userInputByItemId={buildUserInputLookup(form.items)}
          />
        )}
      </div>

      <RpaDrawer.Footer>
        <div className={classes.footer}>
          <Button variant="filled" intent="primary" size="large" loading={isSubmitting} onClick={handleSubmit}>
            {formatSubmitLabel(preview.data?.totalRefund, currency)}
          </Button>
          <button type="button" className={classes.cancelLink} onClick={onClose} disabled={isSubmitting}>
            <Body size="base" weight="semibold" className={classes.cancelText}>
              Cancel
            </Body>
          </button>
        </div>
      </RpaDrawer.Footer>
    </>
  );
}

/** The selector only renders for partial strategies; the others have no item rows. */
function getValueColumn(strategy: RefundUIStrategy | undefined): ValueColumnConfig {
  const config = getRefundOptionConfig(strategy);
  if (config && config.inputType !== "none") {
    return { kind: config.inputType, label: config.itemColumnHeader ?? "" };
  }
  return { kind: "quantity", label: "" };
}

function formatSubmitLabel(totalRefund: number | undefined, currency: GraphQL.SupportedCurrency): string {
  if (totalRefund === undefined) return "Submit Refund";
  return `Submit Refund: ${formatCurrency(currency, totalRefund)}`;
}

function buildUserInputLookup(items: RefundFormState["items"]): Map<number, string> {
  const lookup = new Map<number, string>();
  for (const row of items) {
    const id = row.selectedItem?.id;
    if (id !== undefined) lookup.set(id, row.value);
  }
  return lookup;
}
